/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import style from './limitRulesFilters.style';
import { useAppDispatch, useAppSelector } from '../../../../core/hooks';
import {
  changeFilters,
  Filters,
  selectEmployeeTypes,
  selectSortedCountries,
  selectFilters,
  selectSortedBrands,
  fetchFilters,
  selectStoreTypologies,
} from '../stores/limitRulesFiltersSlice';
import { Brand } from '../../../../shared/models/brand';
import { RadioGroup, RadioItem } from '../../../../shared/components/RadioGroup';
import { ModalPage } from '../../../../shared/components/ModalPage';

export const LimitRulesFilters = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);
  const [countryFilter, setCountryFilter] = useState(filters.country);
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState(filters.employeeType);
  const [brandFilter, setBrandFilter] = useState(filters.brand);
  const [storeTypologyFilter, setStoreTypologyFilter] = useState(filters.storeTypology)
  const [open, setOpen] = React.useState(false);
  dispatch(fetchFilters());
  useEffect(() => {
    setCountryFilter(filters.country);
    setEmployeeTypeFilter(filters.employeeType);
    setBrandFilter(filters.brand);
    setStoreTypologyFilter(filters.storeTypology)
  }, [filters]);

  const applyFilters = () => {
    const filtersToApply: Filters = {
      country: countryFilter,
      employeeType: employeeTypeFilter,
      brand: brandFilter,
      storeTypology: storeTypologyFilter
    };
    dispatch(changeFilters(filtersToApply));
    setOpen(false);
  };

  return (
    <>
      <Icon
        name='filter'
        size='large'
        link
        onClick={() => {
          setOpen(true);
        }}
      />
      {open && (
        <ModalPage
          onClose={() => {
            setOpen(false);
          }}
          title={t('limits.limitRules.filters')}>
          <div css={style.container}>
            <div css={style.filters}>
              <BrandRadioGroup value={brandFilter} onClick={setBrandFilter} />
              <CountryRadioGroup value={countryFilter} onClick={setCountryFilter} />
              <EmployeeTypeRadioGroup value={employeeTypeFilter} onClick={setEmployeeTypeFilter} />
              <StoreTypologyRadioGroup value={storeTypologyFilter} onClick={setStoreTypologyFilter} />
            </div>
            <Button css={style.applyButton} onClick={applyFilters}>
              {t('limits.limitRules.applyFilters')}
            </Button>
          </div>
        </ModalPage>
      )}
    </>
  );
};

interface CountryRadioGroupProps {
  onClick: (code: string) => void;
  value: string;
}

const CountryRadioGroup = ({ value, onClick }: CountryRadioGroupProps): JSX.Element => {
  const { t } = useTranslation();
  const availableCountries = useAppSelector(selectSortedCountries);

  const countries: RadioItem[] = useMemo(
    () => [
      { code: '', description: t('limits.limitRules.allCountries') },
      ...availableCountries.map(c => ({ code: c.description, description: c.description })),
    ],
    [availableCountries, t]
  );

  return (
    <RadioGroup label={t('limits.limitRules.filterByCountry')} items={countries} value={value} onClick={onClick} />
  );
};

interface EmployeeTypeRadioGroupProps {
  onClick: (code: string) => void;
  value: string;
}

const EmployeeTypeRadioGroup = ({ value, onClick }: EmployeeTypeRadioGroupProps): JSX.Element => {
  const { t } = useTranslation();
  const availableEmployeeTypes = useAppSelector(selectEmployeeTypes);

  const employeeTypes: RadioItem[] = useMemo(
    () => [
      { code: '', description: t('limits.limitRules.allEmployeeTypes') },
      ...availableEmployeeTypes.map(e => ({ code: e, description: t(`limits.limitRules.employeeTypes.${e}`) })),
    ],
    [availableEmployeeTypes, t]
  );

  return (
    <RadioGroup
      label={t('limits.limitRules.filterByEmployeeType')}
      items={employeeTypes}
      value={value}
      onClick={onClick}
    />
  );
};

interface BrandRadioGroupProps {
  onClick: (code: string) => void;
  value: string;
}

const BrandRadioGroup = ({ value, onClick }: BrandRadioGroupProps): JSX.Element => {
  const { t } = useTranslation();
  const principalBrands: Brand[] = useAppSelector(selectSortedBrands);

  const brands: RadioItem[] = useMemo(
    () => [
      { code: '', description: t('limits.limitRules.allBrands') },
      ...principalBrands.map(b => ({ code: b.description, description: b.description })),
    ],
    [principalBrands, t]
  );

  return <RadioGroup label={t('limits.limitRules.filterByBrand')} items={brands} value={value} onClick={onClick} />;
};

interface StoreTypologyRadioGroupProps {
  onClick: (code: string) => void;
  value: string;
}


const StoreTypologyRadioGroup = ({ value, onClick }: StoreTypologyRadioGroupProps): JSX.Element => {
  const { t } = useTranslation();
  const typologies = useAppSelector(selectStoreTypologies);

  const storeTypologies: RadioItem[] = useMemo(
    () => [
      { code: '', description: t('limits.limitRules.allTypologies') },
      ...typologies.map(type => ({ code: type, description: type })),
    ],
    [typologies, t]
  );

  return <RadioGroup label={t('limits.limitRules.filterByTypology')} items={storeTypologies} value={value} onClick={onClick} />;
};
